import { useEffect, useMemo, useState } from 'react';
import {
    createTheme,
    Dropdown,
    DropdownDivider,
    DropdownHeader,
    DropdownItem,
} from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'utils/contexts/AuthContext';
import { Link } from 'react-router';
import { ToggleSwitch } from 'shared/Input';
import { usePut } from 'utils/hooks/useAPI';
import rg4js from 'raygun4js';

const theme = createTheme({
    dropdown: {
        floating: {
            item: {
                base: 'focus:bg-gray-100 focus:outline-hidden font-medium gap-1.5 hover:bg-gray-100 inline-flex items-center px-4 py-2 text-gray-900 text-left text-sm w-full visited:text-gray-900',
            },
        },
        inlineWrapper:
            'rounded-full enabled:hover:bg-gray-700 p-2 lg:pr-3 text-gray-200',
    },
}).dropdown;

export const getInitials = (fullName: string) => {
    const names = fullName.split(' ');
    const firstInitial = names.at(0)?.charAt(0).toUpperCase();
    const lastInitial = names.at(-1)?.charAt(0).toUpperCase() ?? '';

    return names.length > 1 ? firstInitial + lastInitial : firstInitial;
};

const UserDropdown = () => {
    const { t } = useTranslation();
    const { logout, account, isEnvOp } = useAuthContext();
    const [lockedToV2LocalState, setLockedToV2LocalState] = useState(true);
    const [_, unsetLockedToV2Flag] = usePut<never>('/helios-api/account/v2');
    const canSwitchToV1 = useMemo(() => {
        const activeUserLockedToV2Flag = account.proxy_user
            ? account.proxy_user.v2
            : account.v2;

        return isEnvOp && activeUserLockedToV2Flag;
    }, [account, isEnvOp]);

    useEffect(() => {
        const resetLockedToV2Flag = async () => {
            await unsetLockedToV2Flag();
            // not Raygun best practice, but sufficient since we just want this occurrence to appear in crash reports
            rg4js('send', {
                error: new Error('User switched back to v1!'),
            });
            window.location.href = '/';
        };

        if (!lockedToV2LocalState && canSwitchToV1) {
            resetLockedToV2Flag();
        }
    }, [lockedToV2LocalState, canSwitchToV1, unsetLockedToV2Flag]);

    const initials = useMemo(() => {
        return getInitials(account.full_name);
    }, [account.full_name]);

    return (
        <Dropdown
            theme={theme}
            inline
            color="light"
            placement="bottom-end"
            arrowIcon={false}
            label={
                <div
                    className="flex items-center gap-2.5 text-xs"
                    aria-label={t('layout:user-menu:aria-label')}
                >
                    <div className="rounded-full bg-gray-100 w-8 h-8 py-2.5 px-2">
                        <p className="leading-3 font-medium text-gray-900">
                            {initials}
                        </p>
                    </div>
                    <div className="hidden lg:flex lg:flex-col">
                        <p className="text-left font-semibold text-sm text-gray-200">
                            {account.full_name}
                        </p>
                    </div>
                </div>
            }
        >
            <DropdownHeader className="px-4 py-2 text-sm">
                <p className="font-semibold">{account.full_name}</p>
                <p>{account.email}</p>
            </DropdownHeader>

            <DropdownItem as={Link} to="/settings">
                <span className="hover:text-primary-700">
                    {t('layout:navigation-links:settings:text')}
                </span>
            </DropdownItem>

            <DropdownItem onClick={logout}>
                <span className="hover:text-primary-700">
                    {t('layout:navigation-links:sign-out:text')}
                </span>
            </DropdownItem>

            {canSwitchToV1 && (
                <>
                    <DropdownDivider />
                    <DropdownItem as="span">
                        <ToggleSwitch
                            value={lockedToV2LocalState}
                            label={t('layout:user-menu.switch-to-v1-toggle')}
                            onChange={(value) => setLockedToV2LocalState(value)}
                        />
                    </DropdownItem>
                </>
            )}
        </Dropdown>
    );
};

export default UserDropdown;
